import type {} from '@mui/x-date-pickers/themeAugmentation';
import { Components, Theme } from '@mui/material/styles';

import { tokens } from './tokens';

const c = tokens.color;

/**
 * Soft status pill per the prototype: pale background + dark text + a small dot
 * in the text colour. Each of the five status steps maps onto one MUI Chip
 * colour; the solid accent fill is never used for status.
 */
const softPill = (bg: string, ink: string) => ({
    backgroundColor: bg,
    color: ink,
    '& .MuiChip-label': { color: ink },
});

export const components: Components<Omit<Theme, 'components'>> = {
    // The calendar shows the week numbers, as the legacy theme did. Purchasing
    // plans deliveries by calendar week and missed them in the opened picker
    // (key-user brief A1.2) — this theme is built from scratch and had lost them.
    MuiDatePicker: {
        defaultProps: {
            displayWeekNumber: true,
        },
    },
    MuiDateTimePicker: {
        defaultProps: {
            displayWeekNumber: true,
        },
    },
    MuiCssBaseline: {
        styleOverrides: {
            body: { backgroundColor: c.canvas, color: c.ink },
        },
    },

    // --- Tables: dense (38/34, prototype 10px horizontal padding) ---
    MuiTableCell: {
        styleOverrides: {
            root: {
                padding: '6px 10px',
                borderBottom: `1px solid ${c.line}`,

                // A row control keeps the row as tall as itself, and the row has
                // a height of its own (38). The general icon button is 30, which
                // with the cell's padding made every row 43 — five pixels of
                // nothing, on every line of every list.
                '& .MuiIconButton-root': {
                    padding: 3,
                },
                '& .MuiCheckbox-root': {
                    padding: 2,
                    '& .MuiSvgIcon-root': {
                        fontSize: '1.375rem',
                    },
                },
            },
            head: {
                height: tokens.size.rowHHead,
                padding: '4px 10px',
                color: c.ink3,
                fontSize: '0.65625rem', // 10.5px
                fontWeight: 620,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                backgroundColor: c.surfaceAlt,
                borderBottom: `1px solid ${c.lineHard}`,
            },
            stickyHeader: {
                backgroundColor: c.surfaceAlt,
            },
            body: {
                height: tokens.size.rowH,
                fontSize: '0.78125rem', // 12.5px
                color: c.ink,
            },
            // A totals row is a row, not a caption: MUI prints it small and grey
            // with the padding of a comfortable table, which left the sums of
            // the bonus settlement detail sitting apart from what they sum up.
            footer: {
                height: tokens.size.rowH,
                padding: '6px 10px',
                fontSize: '0.78125rem', // 12.5px
                fontWeight: 620,
                color: c.ink,
                backgroundColor: c.surfaceAlt,
                borderTop: `1px solid ${c.lineHard}`,
                borderBottom: 'none',
            },
        },
    },

    // --- Status pills (prototype .pill: rounded, h19, 10.5px/620) ---
    MuiChip: {
        styleOverrides: {
            root: {
                height: 19,
                borderRadius: 10,
                fontSize: '0.65625rem', // 10.5px
                fontWeight: 620,
                letterSpacing: '0.015em',
            },
            // MUI pulls the delete cross 6 pixels into the label and draws it at
            // 22 — taller than this 19-pixel chip — so the cross sat on top of
            // the word it belongs to (article search terms, 2026-09-23).
            deleteIcon: {
                fontSize: 13,
                width: 13,
                height: 13,
                margin: '0 4px 0 -2px',
            },
            label: {
                paddingLeft: 8,
                paddingRight: 8,
                display: 'inline-flex',
                alignItems: 'center',
                '&::before': {
                    content: '""',
                    display: 'inline-block',
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    backgroundColor: 'currentColor',
                    marginRight: 5,
                    flexShrink: 0,
                },
            },
            colorDefault: softPill(c.neutralSoft, c.neutralInk), // Entwurf
            colorInfo: softPill(c.accentSoft, c.accentHi), // In Arbeit
            colorWarning: softPill(c.warnSoft, c.warnInk), // Wartet auf mich
            colorError: softPill(c.critSoft, c.crit), // Problem / überfällig
            colorSuccess: softPill(c.okSoft, c.okInk), // Abgeschlossen
        },
    },

    // --- Buttons (prototype .btn: h30, 12.5px/540, radius-s, line-hard border) ---
    MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
            root: {
                textTransform: 'none',
                borderRadius: tokens.radius.small,
                fontWeight: 540,
                fontSize: '0.78125rem', // 12.5px
                minHeight: 30,
                padding: '4px 12px',
                boxShadow: 'none',
                // "Neue Rechnung erstellen" wrapped to two lines and came out 43
                // tall beside 30-pixel buttons. A button is one line; if the
                // label is too long for the room, the label is the problem.
                whiteSpace: 'nowrap',
            },
            // The theme underneath pins every medium button at 40. PALO says 30
            // in `root`, and a fixed height beats a minimum — so the bonus
            // supplier footer drew 40-pixel buttons under a 30-pixel header.
            sizeMedium: {
                height: 'auto',
            },
            containedPrimary: {
                backgroundColor: c.accent,
                color: c.inkInvert,
                '&:hover': { backgroundColor: c.accentHi },
            },
            containedSuccess: {
                backgroundColor: c.ok,
                color: c.inkInvert,
            },
            outlined: {
                borderColor: c.lineHard,
                color: c.ink,
                backgroundColor: c.surface,
                '&:hover': { backgroundColor: c.surfaceAlt, borderColor: c.ink3 },
            },
            text: {
                color: c.ink2,
                '&:hover': { backgroundColor: c.neutralSoft },
            },
        },
    },

    // --- Tabs: underline (accent) ---
    MuiTabs: {
        styleOverrides: {
            // `height: 'auto'` for the same reason as the button above: the
            // theme underneath pins the strip at 40 and its tabs at 38.
            root: { minHeight: 0, height: 'auto' },
            indicator: { backgroundColor: c.accent, height: 2 },
        },
    },
    MuiTab: {
        styleOverrides: {
            root: {
                textTransform: 'none',
                minHeight: 0,
                height: 'auto',
                padding: '8px 13px',
                fontSize: '0.78125rem',
                fontWeight: 600,
                color: c.ink2,
                '&.Mui-selected': { color: c.accent },
            },
        },
    },

    // A select is a field like any other. MUI styles its own inner element with
    // a heavier hand than the outlined input underneath, so the 5x9 padding of
    // every other field lost against 17 — and a select stood 56 pixels tall
    // between 29-pixel neighbours (free-position dialog, 2026-09-23).
    MuiSelect: {
        styleOverrides: {
            select: {
                minHeight: 'unset',
                // The same 19 pixels of content a text field has, so the two
                // stand at exactly one height beside each other.
                height: '19px',
                lineHeight: '19px',
                display: 'flex',
                alignItems: 'center',
                paddingTop: '5px',
                paddingBottom: '5px',
                paddingLeft: '9px',
            },
        },
    },

    // --- Form fields (prototype .f .val.edit: 5x9 padding, line-hard border,
    // radius-s, focused accent border + soft outline glow) ---
    MuiOutlinedInput: {
        styleOverrides: {
            root: {
                borderRadius: tokens.radius.small,
                backgroundColor: c.surface,
                fontSize: '0.8125rem',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: c.lineHard },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: c.ink3 },
                '&.Mui-focused': { boxShadow: `0 0 0 2px ${c.accentSoft}` },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: c.accent, borderWidth: 1 },
                // A refused value outranks the focus ring. The ring was set
                // here and therefore won over it, so a box that refuses what
                // is being typed stayed blue until it lost the focus — which
                // is exactly when nobody is looking at it any more.
                '&.Mui-error': { boxShadow: `0 0 0 2px ${c.critSoft}` },
                '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: c.crit, borderWidth: 1 },
                // A field nobody may type in has to look that way. Only the
                // text was dimmed, so a locked field kept the white ground of
                // an empty one and read as waiting for an entry — on a
                // cancelled invoice most of the mask did (#2231 R-125). Some
                // fields did show grey, which made it worse: the difference
                // looked like it meant something.
                //
                // Read-only counts as locked here. A locked document reaches
                // both states — twelve fields of that invoice were disabled,
                // four read-only and three of those still white — while an
                // editable one carries neither, so the two say the same thing
                // and are drawn the same way.
                '&.Mui-disabled, &.Mui-readOnly': { backgroundColor: c.canvas },
                '&.Mui-disabled .MuiOutlinedInput-notchedOutline, &.Mui-readOnly .MuiOutlinedInput-notchedOutline': {
                    borderColor: c.line,
                },
                '&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline, &.Mui-readOnly:hover .MuiOutlinedInput-notchedOutline':
                    { borderColor: c.line },
                // The ground alone was not enough: a read-only field kept the
                // full-strength text of an editable one, and on the cancelled
                // invoice the three dates read as the only fields still open
                // while the grey text around them read as locked (#2231
                // R-125). A disabled field dims its text by itself; read-only
                // has to be told.
                '&.Mui-readOnly input, &.Mui-readOnly textarea': {
                    color: c.ink3,
                    WebkitTextFillColor: c.ink3,
                },
            },
            input: { padding: '5px 9px' },
            // An icon that sits inside a field has to fit inside it. MUI's
            // adornment button is built for a 56-pixel field and came out 38 in
            // our 29 — taller than the field it belongs to.
            adornedEnd: {
                '& .MuiInputAdornment-root .MuiIconButton-root': {
                    padding: 3,
                    '& .MuiSvgIcon-root': { fontSize: '1.125rem' },
                },
            },
            adornedStart: {
                '& .MuiInputAdornment-root .MuiIconButton-root': {
                    padding: 3,
                    '& .MuiSvgIcon-root': { fontSize: '1.125rem' },
                },
            },
            // A remark is a block, the same height as the drop zone it usually
            // sits beside — a two-row field next to a 144 one read as broken.
            //
            // Only where the field says nothing about its size, though. A field
            // that asks for two rows means two: the purchase order draws the
            // delivery terms and the bonus conditions that way, and the block
            // height turned one line of text into a 144-pixel box beside a
            // 29-pixel select.
            multiline: ({ ownerState }) => {
                const asked =
                    undefined !== ownerState.rows ||
                    undefined !== ownerState.minRows ||
                    undefined !== ownerState.maxRows;

                return {
                    alignItems: 'flex-start',
                    padding: '5px 9px',
                    ...(asked
                        ? {}
                        : {
                              minHeight: tokens.size.blockH,
                              '& textarea': { height: '100% !important' },
                          }),
                };
            },
            // A label that no longer sits in the border needs no gap cut for it.
            notchedOutline: {
                top: 0,
                '& legend': { display: 'none' },
            },
        },
    },
    // --- Icon buttons: as tall as the things they stand beside ---
    //
    // MUI's default is 40 pixels, built for a 56-pixel field. Measured on a
    // contact, that put 38- and 40-pixel buttons next to 29-pixel fields and
    // next to 30-pixel buttons. The rule used to be restated per context — once
    // for table rows, once for the adornments inside a field; this says it once
    // for all of them.
    // A switch beside a 29-pixel field: MUI's medium is 58 by 38, half again
    // as tall as the row it sits in. Its own small variant is the right size
    // and keeps the hit area MUI intends.
    MuiSwitch: {
        defaultProps: { size: 'small' },
    },

    MuiIconButton: {
        styleOverrides: {
            root: {
                padding: 5,
                '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
            },
            // The theme underneath holds every medium icon button at 40 square.
            // With the padding above that is a 30-pixel button inside a 40-pixel
            // box, and in a table it lifts the whole row by fourteen pixels.
            sizeMedium: {
                minWidth: 0,
                minHeight: 0,
            },
        },
    },

    // --- Field labels: above the field, never floating ---
    //
    // The detail masks draw their own label above the field, per the study's
    // `label { font-size: 11px; color: ink-3; font-weight: 520 }`. Everything
    // that hands MUI a label instead — the filter dialog, the settings, the
    // modals — got MUI's floating one, so the same field wore two different
    // patterns depending on who built it. This makes the theme answer it once.
    MuiInputLabel: {
        styleOverrides: {
            root: {
                position: 'static',
                transform: 'none',
                maxWidth: 'none',
                fontSize: '11px',
                fontWeight: 520,
                lineHeight: '16px',
                color: c.ink3,
                marginBottom: '3px',
                // The floated state is the same label in the same place now.
                '&.MuiInputLabel-shrink': {
                    transform: 'none',
                },
                // An autocomplete renders its label element even without a
                // label. Empty, it still kept the margin and sat its field 3px
                // below the text fields beside it.
                '&:empty': {
                    display: 'none',
                },
                '&.Mui-focused': { color: c.ink3 },
            },
        },
    },

    MuiAutocomplete: {
        styleOverrides: {
            // The next theme pads the inner outlined input from `root`, through
            // a descendant selector that no `inputRoot` override can outweigh.
            // Left standing, every autocomplete under PaloNextThemeProvider came
            // out 48 pixels tall beside 29-pixel selects — two fields in one row
            // that look like two different products (supplier conditions,
            // bonus block). Said here at the same weight, it stands 29.
            root: {
                '& .MuiOutlinedInput-root': { padding: '0 9px' },
            },
            // Autocomplete ships its own outlined-input paddings that beat the
            // MuiOutlinedInput override above; restate the compact metrics here.
            inputRoot: {
                padding: '0 9px',
                // Autocomplete adds room for its own buttons and grew five
                // pixels past every field beside it.
                minHeight: 29,
                '& .MuiAutocomplete-endAdornment .MuiIconButton-root': {
                    padding: 3,
                    '& .MuiSvgIcon-root': { fontSize: '1.125rem' },
                },
                // Autocomplete pads its input from a three-class selector, which
                // beats the `input` slot below however the slot is written. Said
                // at the same weight, the field stays 29 instead of growing to 34
                // beside the ones it shares a row with.
                '&.MuiOutlinedInput-root .MuiAutocomplete-input': {
                    padding: '5px 0',
                },
            },
            input: {
                padding: '5px 0',
            },
            paper: {
                borderRadius: tokens.radius.default,
                border: `1px solid ${c.line}`,
                boxShadow: tokens.shadow.s2,
                backgroundColor: c.surface,
            },
            listbox: {
                padding: '4px',
                '& .MuiAutocomplete-option': {
                    fontSize: '0.8125rem',
                    borderRadius: tokens.radius.small,
                    padding: '6px 10px',
                    minHeight: 0,
                    '&.Mui-focused': { backgroundColor: c.accentSoft },
                    '&[aria-selected="true"]': { backgroundColor: c.accentSoft, color: c.accentHi },
                },
            },
        },
    },

    // --- Menus (Select dropdowns, row menus) ---
    MuiMenu: {
        styleOverrides: {
            paper: {
                borderRadius: tokens.radius.default,
                border: `1px solid ${c.line}`,
                boxShadow: tokens.shadow.s2,
                backgroundColor: c.surface,
            },
            list: { padding: '4px' },
        },
    },
    MuiMenuItem: {
        styleOverrides: {
            root: {
                fontSize: '0.8125rem',
                borderRadius: tokens.radius.small,
                padding: '6px 10px',
                minHeight: 0,
                '&:hover': { backgroundColor: c.accentSoft },
                '&.Mui-selected': { backgroundColor: c.accentSoft, color: c.accentHi },
                '&.Mui-selected:hover': { backgroundColor: c.accentSoft },
            },
        },
    },

    // --- Cards / panels ---
    MuiPaper: {
        styleOverrides: {
            root: { backgroundImage: 'none' },
        },
    },
    MuiCard: {
        styleOverrides: {
            root: {
                border: `1px solid ${c.line}`,
                borderRadius: tokens.radius.default,
                boxShadow: tokens.shadow.s1,
                backgroundImage: 'none',
            },
        },
    },
    MuiCardHeader: {
        styleOverrides: {
            root: {
                minHeight: 36,
                padding: '0 13px',
                borderBottom: `1px solid ${c.line}`,
                backgroundColor: c.surfaceAlt,
            },
            title: { fontSize: '0.78125rem', fontWeight: 600 },
        },
    },
};
